"use client";
import { useState, useRef, useEffect } from "react";
import styles from "./Chatbot.module.scss";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { TbMessageChatbot } from "react-icons/tb";
import { IoClose, IoSendSharp } from "react-icons/io5";
import Image from "next/image";
export default function ChatWidget() {
  const prompts = ["Want to explore new arrivals?", "See today's best deals?"];

  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState([
    { from: "bot", text: "Hi! How can I help you today?" },
  ]);

  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const inputRef = useRef(null);
  const handleClickOutside = (e) => {
    if (chatBoxRef.current && !chatBoxRef.current.contains(e.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (open) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => document.removeEventListener("click", handleClickOutside);
  }, [chat, open]);

  // Focus input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && open) setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function ask(q) {
    if (q.trim() === "" || isProcessing) return;
    setChat((c) => [...c, { from: "user", text: q }]);
    try {
      setIsProcessing(true);
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question: q }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to fetch answer");
      }

      setChat((c) => [
        ...c,
        {
          from: "bot",
          text: data.answer ?? "I couldn't generate a reply.",
        },
      ]);
    } catch {
      setChat((c) => [
        ...c,
        {
          from: "bot",
          text: "Oops, something went wrong. Please try again later.",
        },
      ]);
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <>
      <button
        aria-label="Toggle chatbot"
        title="Chat with Geer Assistant"
        onClick={() => setOpen((v) => !v)}
        className={styles.chatBtn}
      >
        <Image
          src={"/favicon.ico"}
          width={40}
          height={40}
          alt="Geer Assistant"
        />
      </button>
      {open && (
        <div className={`${styles.chatBox} ${styles.open}`} ref={chatBoxRef}>
          <nav className={styles.header}>
            <div className={styles.brand}>
              <div className={styles.brandAvatar} aria-hidden>
                <Image
                  src={"/favicon.ico"}
                  width={40}
                  height={40}
                  alt="Geer Assistant"
                />
              </div>
              <div className={styles.brandText}>
                <span className={styles.brandName}>Geer Assistant</span>
                <span className={styles.brandStatus}>
                  <span className={styles.statusDot} /> Online
                </span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className={styles.closeButton}
              aria-label="Close chatbot"
              title="Close"
            >
              <IoClose />
            </button>
          </nav>

          <div className={styles.history} role="log" aria-live="polite">
            {chat.map((msg, i) => (
              <div
                key={i}
                className={`${styles.message} ${
                  msg.from === "user" ? styles.user : styles.bot
                }`}
              >
                {msg.from === "bot" && (
                  <div className={styles.avatar} aria-hidden>
                    <Image
                      src={"/favicon.ico"}
                      width={40}
                      height={40}
                      alt="Geer Assistant"
                    />
                  </div>
                )}
                <div className={styles.bubble}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({ ...props }) => <span {...props} />,
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className={`${styles.message} ${styles.bot}`}>
                <div className={styles.avatar} aria-hidden>
                  <TbMessageChatbot />
                </div>
                <div
                  className={`${styles.bubble} ${styles.typing}`}
                  aria-label="Assistant is typing"
                >
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className={styles.prompts}>
            {chat.length === 1 &&
              prompts.map((p, i) => (
                <button key={i} onClick={() => ask(p)} disabled={isProcessing}>
                  {p}
                </button>
              ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (isProcessing) return;
              ask(input.trim());
              setInput("");
            }}
            className={styles.inputArea}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                isProcessing
                  ? "Thinking..."
                  : "Ask about products, styles, deals..."
              }
              className={styles.textInput}
              aria-label="Message"
              disabled={isProcessing}
            />
            <button
              aria-label="Send message"
              type="submit"
              className={styles.sendButton}
              disabled={isProcessing || !input.trim()}
              title={isProcessing ? "Please wait" : "Send"}
            >
              <IoSendSharp />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
